import React, { useState, useEffect, useCallback } from "react";
import { BrandButton, ThemePrimaryButton, TextCancelButton } from "../button";
import axios from "axios";
import {
  Box,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  InputAdornment,
  Alert,
  Snackbar,
  LinearProgress,
  Tooltip,
  ButtonGroup,
  styled,
} from "@mui/material";
import {
  Edit,
  Search,
  Add,
  Visibility,
  GridView,
  ViewList,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ConfirmDeleteDialog from "../common/ConfirmDeleteDialog";
import {
  PageShell,
  PageHeader,
  PageBlockTitle,
  Card as PageCard,
  CardContent as PageCardContent,
  Table as AcmeTable,
  Input,
  Button,
  EmptyState,
  MenuItem,
  TableActionsMenu,
  TableActionsMenuButton,
  destructiveMenuItemSx,
  stopTableRowClick,
} from "../ui";
import {
  acmePageSectionCardSx,
  acmeBillingSectionContentSx,
  acme,
} from "../../theme/acmeTheme";

// Create scoped styled components
const TemplateCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "all 0.3s ease",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
  borderRadius: "8px",
  overflow: "hidden",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: "0 5px 15px rgba(0, 0, 0, 0.15)",
  },
}));

const TemplateCardMedia = styled(CardMedia)(({ theme }) => ({
  height: "200px",
  overflow: "hidden",
  position: "relative",
  backgroundColor: "#f5f5f5",
  borderBottom: "1px solid #eee",
  "@media (max-width: 960px)": {
    height: "180px",
  },
  "@media (max-width: 600px)": {
    height: "150px",
  },
}));

const TemplateCardContent = styled(CardContent)(() => ({
  flexGrow: 1,
  padding: "12px 14px",
}));

const TemplateCardActions = styled(CardActions)(({ theme }) => ({
  padding: "4px 2px",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: 0,
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: "#f9f9f9",
  borderTop: "1px solid #eee",
  cursor: "default",
  "& .MuiIconButton-root": {
    padding: theme.spacing(0.5),
  },
}));

const TemplatePreview = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none", // Prevent clicking on preview elements
  overflow: "hidden",
}));

const TemplatePreviewDialog = styled("div")(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "70vh",
  minHeight: "500px",
  backgroundColor: "#f8f9fa",
  overflow: "hidden",
  border: "1px solid #e1e5e9",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  display: "flex",
  flexDirection: "column",
}));

const PreviewHeader = styled("div")(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderBottom: "1px solid #e1e5e9",
  padding: "16px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "& .preview-title": {
    fontSize: "16px",
    color: "#2c3e50",
    margin: 0,
  },
  "& .preview-badge": {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
    padding: "4px 12px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: 500,
  },
}));

const PreviewContent = styled("div")(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  backgroundColor: "#ffffff",
  "& iframe, & > div": {
    width: "100%",
    border: "none",
    display: "block",
  },
}));

const GridContainer = styled(Grid)(({ theme }) => ({
  animation: "fadeIn 0.3s ease-in-out",
  "@keyframes fadeIn": {
    from: {
      opacity: 0,
      transform: "translateY(20px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

// Этот контейнер нужен для корректного масштабирования
const PreviewScaler = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "1200px", // Стандартная ширина email шаблона
  height: "1500px", // Примерная высота
  transform: "translate(-50%, -50%) scale(0.13)", // Масштабирование для карточки
  transformOrigin: "center center",
  backgroundColor: "#fff",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  "@media (max-width: 1280px)": {
    transform: "translate(-50%, -50%) scale(0.12)",
  },
  "@media (max-width: 960px)": {
    transform: "translate(-50%, -50%) scale(0.11)",
  },
  "@media (max-width: 600px)": {
    transform: "translate(-50%, -50%) scale(0.10)",
  },
}));

const Template = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [renameDialog, setRenameDialog] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [templateActionMenu, setTemplateActionMenu] = useState({
    anchorEl: null,
    template: null,
  });

  const showNotification = useCallback((message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/templates");
      setTemplates(response.data);
      setFilteredTemplates(response.data);
    } catch (error) {
      console.error("Failed to load templates:", error);
      showNotification("Error loading templates", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  // Load templates on first render
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Filter templates when search query changes
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredTemplates(templates);
    } else {
      const filtered = templates.filter((template) =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredTemplates(filtered);
    }
  }, [searchQuery, templates]);

  // Create template with Mailsball Editor Pro
  const handleCreateTemplateWithMailsballPro = () => {
    navigate("/templates/editor-pro");
  };

  // Edit template using Mailsball Editor Pro
  const handleEditTemplateWithMailsballPro = (template) => {
    // Pass template data via state so it can be loaded in the editor
    navigate(`/templates/editor-pro/${template._id}`, {
      state: {
        templateData: {
          id: template._id,
          name: template.name,
          html: template.html,
          css: template.css,
          blocks: template.blocks || [],
          settings: template.settings || {},
        },
      },
    });
  };

  // Duplicate template
  const handleDuplicate = async (template) => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Create duplicate copy
      const duplicateTemplate = {
        name: `${template.name} (Copy)`,
        html: template.html,
        css: template.css,
        blocks: template.blocks || [],
        settings: template.settings || {},
      };

      await axios.post("/api/templates", duplicateTemplate, config);

      // Update templates and show notification
      fetchTemplates();
      showNotification("Template duplicated successfully");
    } catch (error) {
      console.error("Failed to duplicate template:", error);
      showNotification("Error duplicating template", "error");
    }
  };

  const openDeleteConfirm = (template) => {
    setTemplateToDelete(template);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setTemplateToDelete(null);
  };

  // Delete template
  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t._id !== id));
      setFilteredTemplates((prev) => prev.filter((t) => t._id !== id));
      closeDeleteConfirm();
      showNotification("Template deleted");
    } catch (error) {
      console.error("Failed to delete template:", error);
      showNotification("Error deleting template", "error");
    }
  };

  const handleConfirmDelete = () => {
    if (templateToDelete) {
      handleDelete(templateToDelete._id);
    }
  };

  // Rename template
  const handleRename = async (id) => {
    try {
      // Find current template
      const template = templates.find((t) => t._id === id);

      if (!template) {
        console.error(`Template with ID ${id} not found`);
        return;
      }

      // Update template data
      const updatedTemplate = {
        name: renameValue,
        html: template.html,
        css: template.css,
      };

      // Send PUT request
      await axios.put(`/api/templates/${id}`, updatedTemplate);

      // Update local states
      setTemplates((prev) =>
        prev.map((t) => (t._id === id ? { ...t, name: renameValue } : t)),
      );
      setFilteredTemplates((prev) =>
        prev.map((t) => (t._id === id ? { ...t, name: renameValue } : t)),
      );

      setRenameDialog(false);
      showNotification("Template renamed");
    } catch (error) {
      console.error("Failed to rename template:", error);
      showNotification("Error renaming template", "error");
    }
  };

  // Open template preview
  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setPreviewDialog(true);
  };

  const openTemplateActionMenu = (event, template) => {
    event.stopPropagation();
    setTemplateActionMenu({ anchorEl: event.currentTarget, template });
  };

  const closeTemplateActionMenu = () => {
    setTemplateActionMenu({ anchorEl: null, template: null });
  };

  const handleTemplateActionMenuItem = (action) => {
    const template = templateActionMenu.template;
    if (!template) return;
    closeTemplateActionMenu();

    if (action === "preview") {
      handlePreview(template);
      return;
    }
    if (action === "duplicate") {
      handleDuplicate(template);
      return;
    }
    if (action === "edit") {
      handleEditTemplateWithMailsballPro(template);
      return;
    }
    if (action === "rename") {
      setRenameDialog(true);
      setRenameValue(template.name);
      setCurrentTemplate(template);
      return;
    }
    if (action === "delete") {
      openDeleteConfirm(template);
    }
  };

  // Create preview from HTML for dialog
  const createDialogPreviewFromHtml = (html) => {
    if (!html) return "";

    // Clean HTML and prepare for preview
    let previewHtml = html.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      "",
    );

    // Add responsive meta tags and styles for better mobile viewing
    const responsiveStyles = `
      <style>
        body { 
          margin: 0; 
          padding: 0; 
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
          background-color: #ffffff;
        }
        table { 
          border-collapse: collapse; 
          width: 100%; 
          max-width: 100%;
        }
        img { 
          max-width: 100%; 
          height: auto; 
          display: block;
        }
        @media (max-width: 600px) {
          .email-container {
            width: 100% !important;
            max-width: 100% !important;
          }
          .mobile-center {
            text-align: center !important;
          }
        }
      </style>
    `;

    // Wrap content in a container for better presentation
    const wrappedHtml = `
      <div class="email-container" style="width: 100%; margin: 0 auto; background: #ffffff; box-sizing: border-box;">
        ${previewHtml}
      </div>
    `;

    return (
      <div
        style={{
          width: "100%",
          minHeight: "400px",
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "100%",
            margin: "0 auto",
          }}
          dangerouslySetInnerHTML={{ __html: responsiveStyles + wrappedHtml }}
        />
      </div>
    );
  };

  // Create preview from HTML for card
  const createPreviewFromHtml = (html) => {
    // Safe preview creation (can add additional checks)
    if (!html) return "";

    // Extract only main content for preview
    const previewHtml = html.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      "",
    );

    // Return preview with container for proper scaling
    return (
      <TemplatePreview>
        <PreviewScaler dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </TemplatePreview>
    );
  };

  // Grid view render
  const renderGridView = () => (
    <GridContainer container spacing={2}>
      {loading ? (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      ) : filteredTemplates.length === 0 ? (
        <Grid item xs={12}>
          <TemplatesEmptyState searchQuery={searchQuery} />
        </Grid>
      ) : (
        filteredTemplates.map((template) => (
          <Grid item xs={6} sm={4} md={3} lg={2} xl={1.5} key={template._id}>
            <TemplateCard onClick={() => handlePreview(template)}>
              <TemplateCardMedia>
                {createPreviewFromHtml(template.html)}
              </TemplateCardMedia>
              <TemplateCardContent>
                <Typography
                  noWrap
                  title={template.name}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.8125rem",
                    lineHeight: 1.4,
                    color: acme.colors.foreground,
                  }}
                >
                  {template.name}
                </Typography>
              </TemplateCardContent>
              <TemplateCardActions onClick={stopTableRowClick}>
                <TableActionsMenuButton
                  ariaLabel="Template actions"
                  onClick={(event) => openTemplateActionMenu(event, template)}
                />
              </TemplateCardActions>
            </TemplateCard>
          </Grid>
        ))
      )}
    </GridContainer>
  );

  // List view render
  const renderListView = () => (
    <AcmeTable>
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={2}>
              <LinearProgress />
            </TableCell>
          </TableRow>
        ) : filteredTemplates.length === 0 ? (
          <TableRow>
            <TableCell colSpan={2} style={{ border: "none", padding: 0 }}>
              <TemplatesEmptyState searchQuery={searchQuery} />
            </TableCell>
          </TableRow>
        ) : (
          filteredTemplates.map((template) => (
            <TableRow key={template._id}>
              <TableCell>{template.name}</TableCell>
              <TableCell align="right" onClick={stopTableRowClick}>
                <TableActionsMenuButton
                  ariaLabel="Template actions"
                  onClick={(event) => openTemplateActionMenu(event, template)}
                />
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </AcmeTable>
  );

  return (
    <PageShell>
      <PageHeader
        title="Templates"
        description="Create and manage email templates for your campaigns and reuse them when composing mailings."
        action={
          <Button
            variant="default"
            startIcon={<Add />}
            onClick={handleCreateTemplateWithMailsballPro}
          >
            Create Template
          </Button>
        }
        sx={{ mb: 2 }}
      />

      <PageCard sx={{ ...acmePageSectionCardSx, boxShadow: "none", mt: 0 }}>
        <PageCardContent sx={acmeBillingSectionContentSx}>
          <PageBlockTitle gutterBottom>Templates</PageBlockTitle>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Input
              sx={{ flexGrow: 1, marginRight: 2 }}
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <ButtonGroup variant="outlined">
              <Tooltip title="Grid view">
                <IconButton
                  color={viewMode === "grid" ? "primary" : "default"}
                  onClick={() => setViewMode("grid")}
                >
                  <GridView />
                </IconButton>
              </Tooltip>
              <Tooltip title="List view">
                <IconButton
                  color={viewMode === "list" ? "primary" : "default"}
                  onClick={() => setViewMode("list")}
                >
                  <ViewList />
                </IconButton>
              </Tooltip>
            </ButtonGroup>
          </Box>

          {viewMode === "grid" ? renderGridView() : renderListView()}
        </PageCardContent>
      </PageCard>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        onConfirm={handleConfirmDelete}
        itemLabel="template"
      />

      {/* Rename Dialog */}
      <Dialog open={renameDialog} onClose={() => setRenameDialog(false)}>
        <DialogTitle>Rename Template</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            autoFocus
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <TextCancelButton onClick={() => setRenameDialog(false)}>
            Cancel
          </TextCancelButton>
          <ThemePrimaryButton
            onClick={() => handleRename(currentTemplate._id)}
            disabled={!renameValue.trim()}
          >
            Save
          </ThemePrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #e1e5e9",
            py: 2,
            px: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Visibility sx={{ color: "#6c757d" }} />
            <Typography variant="h6" sx={{ color: "#2c3e50" }}>
              {previewTemplate?.name || "Template Preview"}
            </Typography>
          </Box>
          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <Box
              sx={{
                backgroundColor: "#e8f5e8",
                color: "#2e7d32",
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              Live Preview
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <TemplatePreviewDialog>
            <PreviewHeader>
              <div className="preview-title">
                {previewTemplate?.name || "Template Preview"}
              </div>
              <div className="preview-badge">Preview</div>
            </PreviewHeader>
            <PreviewContent>
              {previewTemplate &&
                createDialogPreviewFromHtml(previewTemplate.html)}
            </PreviewContent>
          </TemplatePreviewDialog>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#f8f9fa",
            borderTop: "1px solid #e1e5e9",
            p: 3,
            gap: 2,
          }}
        >
          <TextCancelButton
            onClick={() => setPreviewDialog(false)}
            sx={{
              color: "#6c757d",
              "&:hover": { backgroundColor: "#e9ecef" },
            }}
          >
            Close
          </TextCancelButton>
          <BrandButton
            primaryHover="light"
            onClick={() => {
              setPreviewDialog(false);
              handleEditTemplateWithMailsballPro(previewTemplate);
            }}
            variant="contained"
            sx={{
              px: 3,
              py: 1,
              borderRadius: "8px",
            }}
            startIcon={<Edit />}
          >
            Edit Template
          </BrandButton>
        </DialogActions>
      </Dialog>

      <TableActionsMenu
        anchorEl={templateActionMenu.anchorEl}
        open={Boolean(templateActionMenu.anchorEl)}
        onClose={closeTemplateActionMenu}
      >
        <MenuItem onClick={() => handleTemplateActionMenuItem("preview")}>
          Preview
        </MenuItem>
        <MenuItem onClick={() => handleTemplateActionMenuItem("duplicate")}>
          Duplicate
        </MenuItem>
        <MenuItem onClick={() => handleTemplateActionMenuItem("edit")}>
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleTemplateActionMenuItem("rename")}>
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => handleTemplateActionMenuItem("delete")}
          sx={destructiveMenuItemSx}
        >
          Delete
        </MenuItem>
      </TableActionsMenu>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

function TemplatesEmptyState({ searchQuery }) {
  return (
    <EmptyState
      icon={DescriptionIcon}
      title={searchQuery ? "No templates found" : "No email templates yet"}
      description={
        searchQuery
          ? "Try changing your search query to find the template you're looking for."
          : "Create your first email template using our visual editor. Design beautiful, responsive emails for your marketing campaigns with drag-and-drop simplicity."
      }
      hint={
        searchQuery
          ? "Clear the search to see all templates."
          : 'Click "Create Template" above to get started.'
      }
    />
  );
}

export default Template;
